import { Injector, LOCALE_ID, NgModule, isDevMode, inject, provideAppInitializer, DOCUMENT } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  provideNativeDateAdapter,
  RippleGlobalOptions
} from '@angular/material/core';
import { InteractivityChecker } from '@angular/cdk/a11y';
// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { UIRouterModule } from '@uirouter/angular';
// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { UIRouter } from '@uirouter/core';
import { TranslateModule } from '@ngx-translate/core';
import { provideHighcharts } from 'highcharts-angular';
import {
  ABOUT_DIALOG_CONFIGURATION,
  ANIMATION_CONFIG_PROVIDER_FACTORY,
  ANIMATION_CONFIGURATION,
  BOOTSTRAP_CONFIGURATION,
  BootstrapInitFactory,
  BootstrapService,
  ComponentsModule,
  ConfigurationService,
  CurrencyPipe,
  DatePipe,
  EXPORT_CONFIGURATION,
  HELP_LINK_COMPONENT_TYPE,
  LINK_REGISTRY_CONFIGURATION,
  LOCALE_CONFIGURATION,
  MODAL_HEADER_CONFIG,
  NumberPipe,
  PAGE_TITLE_SERVICE_CONFIGURATION,
  REST_CONFIGURATION,
  REST_FRAMEWORK_CONFIGURATION,
  SECURITY_CONFIGURATION,
  ServicesModule,
  SESSION_CONFIGURATION,
  STORAGE_KEY_BUILDER_CONFIGURATION,
  THEMES,
  TIMEZONE_CONFIGURATION,
  ERROR_LOG_LOCAL_CONFIGURATION,
  CONSUMER_TYPE,
  ConsumerType,
  CONSUMERS,
  SYSTEM_STATUS_CONFIGURATION,
  SystemStatusChangeService,
  MENU_CONFIGURATION,
  ROUTER,
  AppRouter,
  MODAL_NOTIFICATION_CONFIGURATION,
  FEATURE_FLAGS,
  ANALYST_RATING_CONFIGURATION,
  ANALYTICS_CONFIGURATION,
  BRIGHTCOVE_CONFIGURATION,
  CLIENT_PROFILE_CONFIGURATION,
  ESERVICES_CONFIGURATION,
  EXCHANGE_RATES_CONFIGURATION,
  HelpComponent,
  PAGE_OFFSET_CONFIGURATION,
  PREFERENCES_CONFIGURATION,
  PreferencesService,
  WEBSOCKET_CONFIGURATION,
  InteractivityCheckerService,
  WebbrokerResponsiveService,
  CFR_CONFIGURATION,
  ONE_TRUST_CONFIGURATION,
  DATA_ANALYTICS_TAGGING_CONFIGURATION,
  W8BEN_CONFIGURATION,
  RouterBaseService,
  GAML_CONFIGURATION,
  GamlAttestationAnalytics, WINDOW_TOKEN
} from '@tdwb/core';

import { HOME_PAGE_BALANCE_TREND_PRIMARY_TAB } from '@tdwb/lazy/home';
import {
  uiRouterInitializerFactory,
  UNSUPPORTED_BROWSER_CONFIGURATION,
  ROUTER_UNAUTHORIZED_URL_CONFIGURATION,
  RouterIntegrationService
} from '@tdwb/webbroker/core';

import { TARGETED_AD_CONFIGURATION, WebbrokerCoreModule } from './modules/core/webbroker-core.module';
import { WebbrokerInitNavigationService } from './webbroker-init-navigation.service';
import { WebbrokerInitPageService } from './webbroker-init-page.service';
import { WebbrokerInitService } from './webbroker-init.service';
import { WebbrokerComponent } from './webbroker.component';
import { WebbrokerConfig } from './webbroker.config';
import { WebbrokerRoutes } from './webbroker.routes';
import { TradingModule } from './modules/trading';
import { IntegrationModule } from './modules/platform/integration/integration.module';
import { SiteWideSearchModule } from './modules/platform/site-wide-search/site-wide-search.module';
import { ModalNotificationModule } from './modules/platform/modal-notification/modal-notification.module';
import { PortfolioContactsModule } from './modules/platform/portfolio-contacts/portfolio-contacts.module';

// special use of webbroker embedded on attrader
import { AttraderInitService } from './modules/platform/preferences/attrader/services/attrader-init.service';
import { RouterService } from './modules/core/router/router.service';
import { WebbrokerGamlAttestationAnalytics } from './data-analytics-tagging.constants';
import { AgreementsModule } from './modules/platform/agreements/agreements.module';

// Initializer factory, responsible for handling the configuration call response and initializing proper services
export const initializerFactory = (
  bootstrapService: BootstrapService,
  appRouter: AppRouter,
  injector: Injector,
  consumerType: ConsumerType): () => Promise<unknown> => {

  const isAttrader = consumerType.consumerId === CONSUMERS.CONSUMER_ID_ATTRADER;
  const initializer = isAttrader ? AttraderInitService : WebbrokerInitService;

  bootstrapService.onBootstrapComplete.subscribe({
    next(result: boolean): void {
      if (result) {
        injector.get<WebbrokerInitService | AttraderInitService>(initializer).init();
        appRouter.listenForUrlChange();
      }
    },
    error(): void {
      injector.get<WebbrokerInitService | AttraderInitService>(initializer).init(false);
      appRouter.listenForUrlChange();
    }
  });

  appRouter.disableListenForUrlChange();
  return BootstrapInitFactory(bootstrapService);
};

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true
};
@NgModule({
  declarations: [
    WebbrokerComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ComponentsModule,
    ServicesModule,
    WebbrokerCoreModule,
    UIRouterModule.forRoot({
      useHash: WebbrokerRoutes.useHash,
      config: WebbrokerRoutes.configure,
      states: WebbrokerRoutes.routes
    }),
    TranslateModule.forRoot(),
    TradingModule,
    IntegrationModule,
    ModalNotificationModule,
    PortfolioContactsModule,
    SiteWideSearchModule,
    AgreementsModule
  ],
  // TODO: configure export service
  providers: [
    {
      provide: ROUTER,
      useFactory: uiRouterInitializerFactory,
      deps: [UIRouter, DOCUMENT]
    },
    {
      provide: CONSUMER_TYPE,
      useValue: WebbrokerConfig.getConsumer()
    },
    {
      provide: LOCALE_CONFIGURATION,
      useFactory: WebbrokerConfig.getLocaleConfiguration
    },
    {
      provide: BOOTSTRAP_CONFIGURATION,
      useFactory: WebbrokerConfig.getBootstrapConfig,
      deps: [DOCUMENT, CONSUMER_TYPE]
    },
    provideAppInitializer(() => {
      const initializerFn = (initializerFactory)(inject(BootstrapService), inject(ROUTER), inject(Injector), inject(CONSUMER_TYPE));
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useFactory: WebbrokerConfig.getLocale,
      deps: [ConfigurationService]
    },
    {
      provide: REST_CONFIGURATION,
      useFactory: WebbrokerConfig.getRestConfig,
      deps: [ConfigurationService]
    },
    {
      provide: SECURITY_CONFIGURATION,
      useFactory: WebbrokerConfig.getSecurityConfig,
      deps: [ConfigurationService]
    },
    {
      provide: PREFERENCES_CONFIGURATION,
      useFactory: WebbrokerConfig.getPreferencesConfig,
      deps: [ConfigurationService]
    },
    {
      provide: STORAGE_KEY_BUILDER_CONFIGURATION,
      useFactory: WebbrokerConfig.getStorageKeyBuilderConfig,
      deps: [ConfigurationService]
    },
    {
      provide: LINK_REGISTRY_CONFIGURATION,
      useFactory: WebbrokerConfig.getLinkRegistryConfig,
      deps: [ConfigurationService]
    },
    {
      provide: SESSION_CONFIGURATION,
      useFactory: WebbrokerConfig.getSessionConfig
    },
    {
      provide: EXPORT_CONFIGURATION,
      useFactory: WebbrokerConfig.getExportConfig,
      deps: [ConfigurationService]
    },
    {
      provide: REST_FRAMEWORK_CONFIGURATION,
      useFactory: WebbrokerConfig.getRestFrameworkConfig,
      deps: [CONSUMER_TYPE]
    },
    {
      provide: THEMES,
      useFactory: WebbrokerConfig.getThemes
    },
    {
      provide: ANIMATION_CONFIGURATION,
      useFactory: ANIMATION_CONFIG_PROVIDER_FACTORY
    },
    {
      provide: ABOUT_DIALOG_CONFIGURATION,
      useFactory: WebbrokerConfig.getAboutDialogConfig,
      deps: [ConfigurationService]
    },
    {
      provide: MENU_CONFIGURATION,
      useFactory: WebbrokerConfig.getMenuConfig,
      deps: [ConfigurationService]
    },
    {
      provide: BRIGHTCOVE_CONFIGURATION,
      useFactory: WebbrokerConfig.getBrightcoveConfig,
      deps: [ConfigurationService]
    },
    {
      provide: FEATURE_FLAGS,
      useFactory: WebbrokerConfig.getFeatureFlags,
      deps: [ConfigurationService]
    },
    {
      provide: CLIENT_PROFILE_CONFIGURATION,
      useFactory: WebbrokerConfig.getClientConfig,
      deps: [ConfigurationService]
    },
    {
      provide: EXCHANGE_RATES_CONFIGURATION,
      useFactory: WebbrokerConfig.getExchangeRates,
      deps: [ConfigurationService]
    },
    {
      provide: TARGETED_AD_CONFIGURATION,
      useFactory: WebbrokerConfig.getTargetedAdConfig,
      deps: [ConfigurationService]
    },
    {
      provide: ANALYST_RATING_CONFIGURATION,
      useFactory: WebbrokerConfig.getAnalystRatingConfig,
      deps: [ConfigurationService]
    },
    {
      provide: SYSTEM_STATUS_CONFIGURATION,
      useFactory: WebbrokerConfig.getSystemStatusConfig,
      deps: [ConfigurationService]
    },
    {
      provide: WINDOW_TOKEN,
      useValue: window
    },
    {
      provide: ANALYTICS_CONFIGURATION,
      useFactory: WebbrokerConfig.getAnalyticsConfig,
      deps: [ConfigurationService]
    },
    {
      provide: ESERVICES_CONFIGURATION,
      useFactory: WebbrokerConfig.getEservicesConfig,
      deps: [ConfigurationService]
    },
    {
      provide: ONE_TRUST_CONFIGURATION,
      useFactory: WebbrokerConfig.getOneTrustConfig,
      deps: [ConfigurationService]
    },
    {
      provide: DATA_ANALYTICS_TAGGING_CONFIGURATION,
      useFactory: WebbrokerConfig.getDataAnalyticsTaggingConfig,
      deps: [ConfigurationService]
    },
    {
      provide: HOME_PAGE_BALANCE_TREND_PRIMARY_TAB,
      useFactory: WebbrokerConfig.getHomePageBalanceTrendPrimaryTabEnable,
      deps: [ConfigurationService]
    },
    {
      provide: WEBSOCKET_CONFIGURATION,
      useFactory: WebbrokerConfig.getWebsocketUrl,
      deps: [ConfigurationService, PreferencesService]
    },
    {
      provide: MODAL_NOTIFICATION_CONFIGURATION,
      useFactory: WebbrokerConfig.getModalNotificationConfiguration,
      deps: [ConfigurationService]
    },
    {
      provide: TIMEZONE_CONFIGURATION,
      useFactory: WebbrokerConfig.getTimezoneConfiguration,
      deps: [ConfigurationService]
    },
    {
      provide: ROUTER_UNAUTHORIZED_URL_CONFIGURATION,
      useValue: 'page.errors.403'
    },
    {
      provide: PAGE_TITLE_SERVICE_CONFIGURATION,
      useFactory: WebbrokerConfig.getPageTitleConfiguration,
      deps: [ConfigurationService]
    },
    {
      provide: HELP_LINK_COMPONENT_TYPE,
      useValue: HelpComponent
    },
    {
      provide: PAGE_OFFSET_CONFIGURATION,
      useFactory: WebbrokerConfig.getPageOffsetConfiguration,
      deps: [CONSUMER_TYPE]
    },
    {
      provide: MODAL_HEADER_CONFIG,
      useFactory: WebbrokerConfig.getModalHeaderConfig,
      deps: [CONSUMER_TYPE]
    },
    {
      provide: UNSUPPORTED_BROWSER_CONFIGURATION,
      useFactory: WebbrokerConfig.isBrowserUnsupported,
      deps: [WINDOW_TOKEN]
    },
    {
      provide: CFR_CONFIGURATION,
      useFactory: WebbrokerConfig.getCfrConfig,
      deps: [ConfigurationService]
    },
    {
      provide: InteractivityChecker,
      useClass: InteractivityCheckerService
    },
    {
      // Generate console logs on dev machines only. based on environment.production flag
      provide: ERROR_LOG_LOCAL_CONFIGURATION,
      useFactory: isDevMode
    },
    {
      provide: W8BEN_CONFIGURATION,
      useFactory: WebbrokerConfig.getW8BenConfig,
      deps: [ConfigurationService]
    },
    {
      provide: GAML_CONFIGURATION,
      useFactory: WebbrokerConfig.getGamlConfig,
      deps: [ConfigurationService]
    },
    {
      provide: GamlAttestationAnalytics,
      useClass: WebbrokerGamlAttestationAnalytics
    },
    {
      provide: RouterBaseService,
      useClass: RouterService
    },
    WebbrokerInitService,
    SystemStatusChangeService,
    WebbrokerInitPageService,
    WebbrokerInitNavigationService,
    WebbrokerResponsiveService,
    RouterIntegrationService,
    // TODO of below TODOs: add task to backlog
    CurrencyPipe, // TODO: remove when fixed in core
    DatePipe, // TODO: remove when fixed in core
    NumberPipe, // TODO: remove when fixed in core
    AttraderInitService,
    provideHighcharts({
      instance: () => import('highcharts/esm/highstock').then((m: { default: typeof import('highcharts/esm/highstock').default }) => m.default),
      modules() {
        return [
          import('highcharts/esm/modules/exporting'),
          import('highcharts/esm/modules/export-data'),
          import('highcharts/esm/modules/accessibility'),
          import('highcharts/esm/themes/sunset')
        ]
      }
    }),
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (config: ConfigurationService): string =>
        config.getProperty('locale') === 'fr_CA' ? 'fr' : 'en',
      deps: [ConfigurationService]
    },
    provideNativeDateAdapter(),
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: globalRippleConfig
    }
  ],
  bootstrap: [WebbrokerComponent]
})
export class WebbrokerModule {
}
